import { authService } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface UserData {
  uid: string;
  username: string;
  email: string | null;
  full_name: string | null;
  role: 'admin' | 'analyst' | 'user';
  disabled: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface UserCreate {
  username: string;
  email: string;
  full_name: string;
  password: string;
  role: 'admin' | 'analyst' | 'user';
  disabled: boolean;
}

export interface UserUpdate {
  email?: string;
  full_name?: string;
  password?: string;
  role?: 'admin' | 'analyst' | 'user';
  disabled?: boolean;
}

export interface UserListResponse {
  total: number;
  users: UserData[];
}

class UserManagementService {
  private getHeaders(): Record<string, string> {
    return {
      ...authService.getAuthHeaders(),
      'Content-Type': 'application/json',
    };
  }

  async listUsers(): Promise<UserListResponse> {
    const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Admin access required');
      }
      throw new Error('Failed to fetch users');
    }

    return response.json();
  }

  async getUser(username: string): Promise<UserData> {
    const response = await fetch(`${API_BASE_URL}/api/admin/users/${username}`, {
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`User '${username}' not found`);
      }
      if (response.status === 403) {
        throw new Error('Admin access required');
      }
      throw new Error('Failed to fetch user');
    }

    return response.json();
  }

  async createUser(userData: UserCreate): Promise<UserData> {
    const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.detail || 'Failed to create user';

      if (response.status === 409) {
        throw new Error(errorMessage); // Conflict (username/email already exists)
      }
      if (response.status === 403) {
        throw new Error('Admin access required');
      }
      if (response.status === 422) {
        throw new Error(errorMessage); // Validation error
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async updateUser(username: string, userData: UserUpdate): Promise<UserData> {
    const response = await fetch(`${API_BASE_URL}/api/admin/users/${username}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.detail || 'Failed to update user';

      if (response.status === 404) {
        throw new Error(`User '${username}' not found`);
      }
      if (response.status === 409) {
        throw new Error(errorMessage); // Conflict (email already exists)
      }
      if (response.status === 403) {
        throw new Error('Admin access required');
      }
      if (response.status === 422) {
        throw new Error(errorMessage); // Validation error
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async deleteUser(username: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/admin/users/${username}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.detail || 'Failed to delete user';

      if (response.status === 404) {
        throw new Error(`User '${username}' not found`);
      }
      if (response.status === 403) {
        throw new Error('Admin access required');
      }
      if (response.status === 400) {
        throw new Error(errorMessage); // Cannot delete own account
      }
      throw new Error(errorMessage);
    }
  }
}

export const userManagementService = new UserManagementService();
